import { Box } from '@mui/material';
import { LwTabIndicator } from 'redesign';
import { useIsHqAdmin } from 'shared/hooks';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider';
import { ApplicantTable } from 'shared/components/shift-detail-modal/components/tabs/applicants/applicant-table';
import { PlannerWorkerTableWithSearchbar } from 'partner/components/views/partner-shift-planning/partner-shift-planning-modal/tables/planner-worker-table';
import { EmploymentType } from '@types';
import { ShiftDetail } from 'api/services/shift/schema/shift-detail.schema';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useOgpGetApplicantActions } from './actions/use-ogp-get-applicant-actions';
import { useOgpGetClaimantActions } from './actions/use-ogp-get-claimant-actions';
import { useOgpGetCancelledActions } from './actions/use-ogp-get-cancelled-actions';
import { useGetAddShiftClaimActions } from '../shared/use-get-add-shift-claim-actions';
import { CancelledTable } from '../../components/tabs/cancelled/cancelled-table';
import { ClaimantTable } from '../../components/tabs/claimants/claimant-table';
import { ShiftAddFlexWorker } from '../../components/tabs/add-flex-worker/shift-add-flex-worker';
import { ShiftDetailTab } from '../../components/types';
import { ShiftAddPartnerDataProvider } from '../../components/tabs/shift-add-partner/shift-add-partner';
import { mapShiftDetails } from '../shared/map-shift-details';
import { ShiftDetailsApplicant } from '../../components/tabs/applicants/applicant-table.types';
import { useGetShiftPartners } from '../../query-hooks/hq/use-get-shift-partners';
import { PreferredWorkersTable } from '../../components/tabs/preferred-workers/preferred-worker-table';
import { useGetServiceAgreementsForCompany } from '../../../../../ogp/hooks/queryHooks/service-agreements/use-get-service-agreements-for-company';
import { LogsTabs } from '../../components/shift-logs/logs-tabs/logs-tabs';
import { useGetLogTabs } from '../../components/shift-logs/hooks/use-get-log-tabs/use-get-log-tabs';

export const useOgpGetShiftDetailsTabs = (shiftData: ShiftDetail) => {
  const isHqAdmin = useIsHqAdmin();
  const { getCancelledActions } = useOgpGetCancelledActions();
  const { getClaimantActions, NoShowDialog, ChooseFlexWorkerFlexPools } =
    useOgpGetClaimantActions();
  const { getApplicantActions, BulkAcceptShiftApplicationsDialog } = useOgpGetApplicantActions();
  const flexWorkerTableActions = useGetAddShiftClaimActions({ isHq: false });
  const { allowPartnerRelations, allowPartners } = useFeatureFlagContext();
  const allowPartnerShiftClaimAssign = allowPartners && allowPartnerRelations;
  const getShiftEligiblePartnersQuery = useGetShiftPartners(shiftData.id, true);
  const logTabs = useGetLogTabs(shiftData.history);
  const { t } = useTranslation('web-common');

  const { data: borrowerServiceAgreements } = useGetServiceAgreementsForCompany({
    isBorrower: true,
    isLender: false,
  });
  const claimTableDetails = {
    mutableUntilDate: shiftData.mutableUntilDate,
    isFreelance: shiftData.isFreelance,
    isTempwork: shiftData.isTempwork,
    isEmployee: shiftData.isEmployee,
    id: shiftData.id,
    startDate: shiftData.startDate,
    startTime: shiftData.startTime,
  };
  const applicantTableDetail: ShiftDetailsApplicant = {
    startDate: shiftData.isoStartDateTime,
    employmentType: (shiftData.isFreelance ? 'FREELANCER' : 'TEMPWORKER') as EmploymentType,
    defaultHourlyRate: (shiftData.isFreelance
      ? shiftData.freelanceProperties?.hourlyRateCents
      : shiftData.tempWorkProperties?.grossHourlyRateCents) as number,
    isEmployee: shiftData.isEmployee,
    isFreelance: shiftData.isFreelance,
    isTempwork: shiftData.isTempwork,
  };
  const mappedWorkerData = mapShiftDetails(shiftData);

  if (!mappedWorkerData) {
    return [];
  }

  const now = DateTime.now();
  const startDateTime = DateTime.fromISO(shiftData.isoStartDateTime);

  const isShiftInFuture = now < startDateTime;

  const addWorkerTableDetails = {
    shiftId: shiftData.id,
    isFreelance: shiftData.isFreelance,
    isTempWork: shiftData.isTempwork,
    isEmployee: shiftData.isEmployee,
    isShiftInFuture,
  };

  const showFlexWorkerTab =
    (borrowerServiceAgreements && borrowerServiceAgreements.length > 0) ||
    (addWorkerTableDetails.isEmployee && (isHqAdmin || !isShiftInFuture));

  const showPartnerTab =
    allowPartnerShiftClaimAssign && (shiftData.isFreelance || shiftData.isTempwork);
  const showAddPartnerTab =
    isHqAdmin &&
    getShiftEligiblePartnersQuery.status === 'success' &&
    getShiftEligiblePartnersQuery.data.length > 0 &&
    (shiftData.isFreelance || shiftData.isTempwork);
  const shiftHasActiveVisibilityRules =
    shiftData.visibilityRules &&
    shiftData.visibilityRules.length > 0 &&
    shiftData.visibilityRules.some((rule) => rule.isoRuleEndTime > new Date().toISOString());

  const tabs = [
    {
      id: ShiftDetailTab.TEAM,
      tabLabel: <span data-testid="tab-team">{t('shift_tabs.team')}</span>,
      element: (
        <>
          {NoShowDialog}
          {ChooseFlexWorkerFlexPools}
          <ClaimantTable
            shiftDetails={claimTableDetails}
            shiftClaimants={mappedWorkerData.claimants}
            getActions={getClaimantActions}
          />
        </>
      ),
    },
    {
      id: ShiftDetailTab.APPLICATIONS,
      tabLabel: (
        <>
          {BulkAcceptShiftApplicationsDialog}
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            data-testid="tab-applications"
          >
            {t('shift_tabs.applications')}
            {mappedWorkerData.isThereApplicants ? (
              <LwTabIndicator>{mappedWorkerData.pendingApplicants.length}</LwTabIndicator>
            ) : null}
          </Box>
        </>
      ),
      element: (
        <ApplicantTable
          shiftDetails={applicantTableDetail}
          applicants={mappedWorkerData.applicants}
          getActions={getApplicantActions}
        />
      ),
    },
    {
      id: ShiftDetailTab.CANCELS,
      tabLabel: <span data-testid="tab-cancellations">{t('shift_tabs.cancellations')}</span>,
      element: (
        <CancelledTable
          cancelledCandidates={mappedWorkerData.cancelled}
          getActions={getCancelledActions}
        />
      ),
    },
    ...(shiftData.visibilityRules && shiftData.visibilityRules.length > 0
      ? [
          {
            id: ShiftDetailTab.PREFERRED_WORKERS,
            tabLabel: (
              <span data-testid="tab-preferred-workers">{t('shift_tabs.preferred_workers')}</span>
            ),
            element: <PreferredWorkersTable visibilityRules={shiftData.visibilityRules} />,
          },
        ]
      : []),
    ...(showFlexWorkerTab
      ? [
          {
            id: ShiftDetailTab.ADD,
            tabLabel: (
              <span data-testid="tab-add-flex-worker">{t('shift_tabs.add_flex_worker')}</span>
            ),
            element: (
              <ShiftAddFlexWorker
                shiftDetails={addWorkerTableDetails}
                getRowActions={flexWorkerTableActions}
                isActorOGPUser={!isHqAdmin}
              />
            ),
          },
        ]
      : []),
    ...(showPartnerTab
      ? [
          {
            id: ShiftDetailTab.PARTNERS,
            tabLabel: t('shift_tabs.shift_redirect'),
            element: (
              <ShiftAddPartnerDataProvider
                shiftId={shiftData.id}
                shiftHasActiveVisibilityRules={shiftHasActiveVisibilityRules}
              />
            ),
          },
        ]
      : []),
    ...(showAddPartnerTab
      ? [
          {
            id: ShiftDetailTab.ADD_PARTNER,
            tabLabel: t('shift_tabs.add_partner'),
            element: <PlannerWorkerTableWithSearchbar shiftId={shiftData.id} />,
          },
        ]
      : []),
    {
      id: ShiftDetailTab.LOGS,
      tabLabel: <span data-testid="tab-logs">{t('shift_tabs.logs')}</span>,
      element: <LogsTabs tabs={logTabs} />,
    },
  ];
  return tabs;
};
